/* Shared coalesced cart getter (defined once, first script wins).
   All cart consumers (progress bars, bundle UI, bundle preview) share one
   in-flight /cart.js request + a short-lived cache, kept fresh by the theme's
   cart events and invalidated on real cart mutations. */
(function () {
  if (window.cibCart) return;
  let cached = null;
  let ts = 0;
  let inflight = null;
  const TTL = 700;

  window.cibCart = {
    get() {
      const now = Date.now();
      if (cached && now - ts < TTL) return Promise.resolve(cached);
      if (inflight) return inflight;
      inflight = fetch(((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/') + 'cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then((r) => {
          if (!r.ok) throw new Error('Failed to load cart');
          return r.json();
        })
        .then((cart) => {
          cached = cart;
          ts = Date.now();
          inflight = null;
          return cart;
        })
        .catch((e) => {
          inflight = null;
          throw e;
        });
      return inflight;
    },
    set(cart) {
      if (cart && Array.isArray(cart.items)) {
        cached = cart;
        ts = Date.now();
      }
    },
    invalidate() {
      cached = null;
      ts = 0;
    }
  };

  // Theme cart events carry the fresh post-mutation cart — reuse it.
  ['on:cart:add', 'on:line-item:change'].forEach((ev) => {
    document.addEventListener(ev, (e) => {
      if (e.detail && e.detail.cart) window.cibCart.set(e.detail.cart);
    });
  });
})();

(function () {
  if (window.__cibProgressBarsLoaded) return;
  window.__cibProgressBarsLoaded = true;

  /* ====== CONFIG ====== */
  const BARS_MODE = 3; // 1 ship only, 2 gift only, 3 both
  const SHIPPING_THRESHOLD_CENTS = window.cibShipping?.threshold || 49 * 100;
 const GIFT_THRESHOLD_CENTS = window.fgGlobal?.threshold || 6900;
  const GIFT_URL = "/collections/free-gift-collection";

  /* ====== STATE ====== */
  let isUpdating = false;
  let timer = null;

  let lastSubtotal = null;
  let lastItemCount = null;
  let updateSeq = 0;

  // pending queue for multiple fast updates
  let pending = false;
  let pendingOpts = { force: false };

  // Used to detect "bar is stale" while drawer is open
  let lastRenderedSignature = null; // `${subtotal}|${itemCount}`
  let drawerOpen = false;
  let drawerHealTimerA = null;
  let drawerHealTimerB = null;

  function dollars(cents) {
    return (cents / 100).toFixed(2);
  }

  function desiredBars() {
    return {
      ship: BARS_MODE === 1 || BARS_MODE === 3,
      gift: BARS_MODE === 2 || BARS_MODE === 3
    };
  }

  function isCartMutationUrl(url) {
    // NOTE: /cart.js is a READ, not a mutation. It used to be listed here, which
    // made every cart read (by any script) trigger yet another forced cart fetch
    // — a request storm on each add-to-cart.
    return (
      url.includes("/cart/add") ||
      url.includes("/cart/change") ||
      url.includes("/cart/update") ||
      url.includes("/cart/clear")
    );
  }

  async function getCart() {
    return window.cibCart.get();
  }

  function setProgress(barEl, pct) {
    const fill = barEl?.querySelector(".cib-progress-fill");
    if (fill) fill.style.width = Math.max(0, Math.min(100, pct)) + "%";
  }

  function setState(barEl, ok) {
    if (!barEl) return;
    barEl.classList.remove("cib-bg-warn", "cib-bg-ok");
    barEl.classList.add(ok ? "cib-bg-ok" : "cib-bg-warn");
  }

  function setText(barEl, html) {
    const text = barEl?.querySelector(".cib-progress-text");
    if (text) text.innerHTML = html;
  }

  function setPart(barEl, selector, text) {
    const el = barEl?.querySelector(selector);
    if (el) el.textContent = text;
  }

  // Card copy per state (zero / partial / near >= 90% / unlocked).
  // Mirrors snippets/progress-card.liquid, which renders the first state.
  function barCopy(type, state, remaining, thresholdCents, compact) {
    const left = `$${dollars(remaining)}`;
    const goal = `$${dollars(thresholdCents)}`;
    const link = (label) => `<a href="${GIFT_URL}" class="cib-inline-link">${label}</a>`;

    // Compact cards (cart drawer): one short line, no links — the Free Gift
    // card is itself the link.
    if (compact) {
      if (state === "ok") return type === "gift" ? "Unlocked! Choose your gift" : "Free shipping unlocked!";
      return `Add ${left} more to unlock.`;
    }

    if (type === "ship") {
      if (state === "ok") return `🚚 You've unlocked free shipping!`;
      if (state === "zero") return `🚚 Add items to your cart to earn free shipping!`;
      if (state === "near") return `🚚 Almost there! Just ${left} more for free shipping!`;
      return `🚚 You're ${left} away from free shipping!`;
    }

    if (state === "ok") return `🎉 You've unlocked a Free Gift! ${link("Choose your gift")}`;
    if (state === "zero") return `🎁 Add ${goal} to your cart to unlock a Free Gift!`;
    if (state === "near") return `🎁 So close! Add ${left} more to unlock your Free Gift! ${link("View gifts")}`;
    return `Add ${left} more to unlock your Free Gift 🎁 ${link("View gifts")}`;
  }

  // Loading state on every mount while a cart change is in flight, until the
  // bars show the new totals (animated in assets/cart-page.css). A safety
  // timeout clears it if no update follows.
  // Shown only when the update is slow (> 400ms), like the rest of the cart page.
  // Not "is-loading": the theme draws a spinner on every element with that class.
  let loadingTimer = null;
  let loadingDelay = null;
  function setLoading(on) {
    clearTimeout(loadingTimer);
    clearTimeout(loadingDelay);
    const mounts = document.querySelectorAll("[data-cib-progress]");
    mounts.forEach((mount) => mount.setAttribute("aria-busy", String(on)));

    if (on) {
      loadingDelay = setTimeout(() => {
        mounts.forEach((mount) => mount.classList.add("cib-is-loading"));
      }, 400);
      loadingTimer = setTimeout(() => setLoading(false), 8000);
    } else {
      mounts.forEach((mount) => mount.classList.remove("cib-is-loading"));
    }
  }

  // Restarts the .is-celebrating animation (styled in assets/cart-page.css).
  function celebrate(barEl) {
    barEl.classList.remove("is-celebrating");
    void barEl.offsetWidth; // reflow so the animation can replay
    barEl.classList.add("is-celebrating");
    clearTimeout(barEl._cibCelebrateTimer);
    barEl._cibCelebrateTimer = setTimeout(() => barEl.classList.remove("is-celebrating"), 1600);
  }

  function updateOneBar(barEl, thresholdCents, type, subtotalCents) {
    if (!barEl) return;

    const remaining = Math.max(0, thresholdCents - subtotalCents);
    const pct = Math.min(100, Math.round((subtotalCents / thresholdCents) * 100));
    setProgress(barEl, pct);

    const ok = remaining <= 0;
    const wasUnlocked = barEl.classList.contains("is-unlocked");
    setState(barEl, ok);

    const state = ok ? "ok" : subtotalCents <= 0 ? "zero" : pct >= 90 ? "near" : "partial";
    barEl.classList.toggle("is-unlocked", state === "ok");

    // Just reached the goal (not on page load, where the card is already
    // server-rendered as unlocked): play the one-off "unlocked" animation.
    if (ok && !wasUnlocked) celebrate(barEl);
    barEl.classList.toggle("is-zero", state === "zero");
    barEl.classList.toggle("is-near", state === "near");

    setPart(barEl, ".cib-progress-pct", `${pct}%`);
    setPart(barEl, ".cib-progress-meta-right", `$${dollars(thresholdCents)} goal`);
    const track = barEl.querySelector(".cib-progress-track");
    if (track) track.setAttribute("aria-valuenow", String(pct));

    if (barEl.hasAttribute("data-compact")) {
      setText(barEl, barCopy(type, state, remaining, thresholdCents, true));
    } else {
      // Full cards (cart page) carry the drawer's short copy too, shown on phones
      // by assets/cart-page.css. The short gift "unlocked" copy keeps its link
      // because, unlike the drawer, the card itself isn't one.
      let shortCopy = barCopy(type, state, remaining, thresholdCents, true);
      if (type === "gift" && state === "ok") {
        shortCopy = `Unlocked! <a href="${GIFT_URL}" class="cib-inline-link">Choose your gift</a>`;
      }
      setText(
        barEl,
        `<span class="cib-copy cib-copy--short">${shortCopy}</span>` +
          `<span class="cib-copy cib-copy--full">${barCopy(type, state, remaining, thresholdCents, false)}</span>`
      );
    }
  }

  function updateAllMounts(subtotalCents, itemCount) {
    const want = desiredBars();

    document.querySelectorAll("[data-cib-progress]").forEach((mount) => {
      mount.classList.toggle("is-empty", itemCount === 0);

      const shipBar = mount.querySelector('[data-bar="ship"]');
      const giftBar = mount.querySelector('[data-bar="gift"]');

      if (shipBar) shipBar.style.display = want.ship ? "" : "none";
      if (giftBar) giftBar.style.display = want.gift ? "" : "none";

      if (want.ship) updateOneBar(shipBar, SHIPPING_THRESHOLD_CENTS, "ship", subtotalCents);
      if (want.gift) updateOneBar(giftBar, GIFT_THRESHOLD_CENTS, "gift", subtotalCents);
    });

    // remember what UI should represent
    lastRenderedSignature = `${subtotalCents}|${itemCount}`;
  }

  async function updateUI(opts = {}) {
    // if already running, queue one more run
    if (isUpdating) {
      pending = true;
      pendingOpts = { force: !!(pendingOpts.force || opts.force) };
      return;
    }

    isUpdating = true;
    const mySeq = ++updateSeq;
    const force = !!opts.force;

    try {
      const cart = await getCart();
      if (mySeq !== updateSeq) return;

      const subtotal = Number(cart.total_price || 0);
      const itemCount = Number(cart.item_count || 0);

      // skip only when not forced and nothing changed
      if (!force && subtotal === lastSubtotal && itemCount === lastItemCount) return;

      lastSubtotal = subtotal;
      lastItemCount = itemCount;

      updateAllMounts(subtotal, itemCount);
    } catch (e) {
      // silent
    } finally {
      isUpdating = false;
      if (!pending) setLoading(false);

      if (pending) {
        pending = false;
        const runOpts = pendingOpts;
        pendingOpts = { force: false };
        scheduleUpdate(0, runOpts);
      }
    }
  }

  function scheduleUpdate(delay = 120, opts = {}) {
    clearTimeout(timer);
    timer = setTimeout(() => {
      requestAnimationFrame(() => updateUI(opts));
    }, delay);
  }

  /* =========================================================
     Drawer stale-detection (self heal)
     - Only runs when drawer is open
     - Does 2 checks shortly after open / refresh
     - Refreshes only if signature changed
     ========================================================= */
  async function healIfStale() {
    if (!drawerOpen) return;

    try {
      const cart = await getCart();
      const subtotal = Number(cart.total_price || 0);
      const itemCount = Number(cart.item_count || 0);
      const sig = `${subtotal}|${itemCount}`;

      // if UI signature doesn't match cart signature, force refresh
      if (sig !== lastRenderedSignature) {
        scheduleUpdate(0, { force: true });
      }
    } catch (_) {}
  }

  function scheduleDrawerHealChecks() {
    clearTimeout(drawerHealTimerA);
    clearTimeout(drawerHealTimerB);

    // two short checks to cover "theme re-render after open"
    drawerHealTimerA = setTimeout(healIfStale, 600);
    drawerHealTimerB = setTimeout(healIfStale, 1400);
  }

  /* ====== Hooks ====== */
  const _fetch = window.fetch;
  window.fetch = function () {
    const req = arguments[0];
    const url = String(req?.url || req || "");
    const isMutation = isCartMutationUrl(url);
    if (isMutation) setLoading(true);
    return _fetch.apply(this, arguments).then((res) => {
      try {
        if (isMutation) {
          window.cibCart.invalidate(); // cart changed — drop the shared cache
          scheduleUpdate(120, { force: true });
        }
      } catch (_) {}
      return res;
    }, (err) => {
      if (isMutation) setLoading(false);
      throw err;
    });
  };

  const _open = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this._cibUrl = url;
    return _open.apply(this, arguments);
  };
  const _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (isCartMutationUrl(String(this._cibUrl || ""))) {
      setLoading(true);
      this.addEventListener("error", () => setLoading(false));
    }
    this.addEventListener("load", () => {
      try {
        if (isCartMutationUrl(String(this._cibUrl || ""))) {
          window.cibCart.invalidate();
          scheduleUpdate(120, { force: true });
        }
      } catch (_) {}
    });
    return _send.apply(this, arguments);
  };

  /* ====== Theme events ====== */
  document.addEventListener("dispatch:cart-drawer:open", () => {
    drawerOpen = true;
    scheduleUpdate(0, { force: true });
    scheduleUpdate(260, { force: true });
    scheduleDrawerHealChecks();
  });

  document.addEventListener("dispatch:cart-drawer:refresh", () => {
    drawerOpen = true;
    scheduleUpdate(80, { force: true });
    scheduleDrawerHealChecks();
  });

  /* ====== Drawer visibility watcher (single source of truth) ====== */
  function bindDrawerVisibilityWatcher() {
    const drawer = document.querySelector("#cart-drawer");
    if (!drawer || drawer.__cibBound) return;
    drawer.__cibBound = true;

    function isVisible() {
      const ariaHidden = drawer.getAttribute("aria-hidden");
      const hasOpen = drawer.hasAttribute("open");
      return ariaHidden === "false" || hasOpen;
    }

    let lastVis = isVisible();

    const mo = new MutationObserver(() => {
      const vis = isVisible();

      if (vis && !lastVis) {
        drawerOpen = true;
        scheduleUpdate(0, { force: true });
        scheduleUpdate(260, { force: true });
        scheduleDrawerHealChecks();
      }

      if (!vis) drawerOpen = false;

      lastVis = vis;
    });

    mo.observe(drawer, { attributes: true, attributeFilter: ["aria-hidden", "open"] });
  }

  /* ====== Cart icon click fallback (generic) ====== */
  function bindCartIconClick() {
    if (window.__cibCartClickBound) return;
    window.__cibCartClickBound = true;

    document.addEventListener(
      "click",
      (e) => {
        const el = e.target?.closest?.(
          "#cart-icon-desktop, #cart-icon, a[href='/cart'], a[href='/cart/'], [data-cart-drawer-open], .js-open-cart, .header__cart"
        );
        if (!el) return;

        // assume it will open; do quick sync
        scheduleUpdate(0, { force: true });
        scheduleUpdate(260, { force: true });
        scheduleDrawerHealChecks();
      },
      true
    );
  }

  document.addEventListener("DOMContentLoaded", () => {
    scheduleUpdate(120, { force: true });
    bindDrawerVisibilityWatcher();
    bindCartIconClick();

    // In case drawer mounts after load (only retry binding, no body subtree observer)
    setTimeout(bindDrawerVisibilityWatcher, 600);
  });

  // Manual
  window.updateCIBProgressBars = () => scheduleUpdate(0, { force: true });
})();
